import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CURRENT_LEGAL_VERSION, LEGAL_URLS } from '../config/legal';
import { Mail, Lock, User, AlertCircle, CheckCircle, Building2, Users } from 'lucide-react';
import {
  SEAT_MIN,
  SEAT_MAX,
  clampSeats,
  annualDiscountPercent,
} from '../lib/enterprisePricing';

type Plan = 'trial' | 'unlimited' | 'enterprise';
type BillingCycle = 'monthly' | 'annual';

interface SignupFormProps {
  onSwitchToLogin: () => void;
  onForgotPassword: () => void;
  firmInvite?: { firm_name: string; invited_email: string } | null;
}

function getInitialPlan(): Plan {
  const param = new URLSearchParams(window.location.search).get('plan');
  if (param === 'unlimited' || param === 'enterprise') return param;
  return 'trial';
}

export function SignupForm({ onSwitchToLogin, onForgotPassword, firmInvite }: SignupFormProps) {
  const { signUp, resendConfirmation } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<Plan>(getInitialPlan);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(firmInvite?.invited_email ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Enterprise-only fields
  const [firmName, setFirmName] = useState('');
  const [seatCount, setSeatCount] = useState(SEAT_MIN);
  // Admin-first: creating a firm makes you its admin, which is free. Whether
  // you also take one of the paid seats is a separate question, defaulted on
  // because most people setting up a firm intend to use it.
  const [takeSeat, setTakeSeat] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setAccountExists(false);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    if (!agreed) {
      setError('You must agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);

    const { error: signUpError, userId, alreadyRegistered } = await signUp(email, password, fullName);

    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
    } else if (alreadyRegistered) {
      // No email was sent, no account was created: send them to sign in.
      setAccountExists(true);
      setLoading(false);
    } else {
      // Record clickwrap acceptance of Terms + Privacy (best-effort; the
      // re-acknowledgment prompt backstops any failure here).
      if (userId) {
        try {
          await fetch('/.netlify/functions/record-acceptance', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId,
              documentTypes: ['terms', 'privacy'],
              version: CURRENT_LEGAL_VERSION,
            }),
          });
        } catch (err) {
          console.error('Failed to record document acceptance:', err);
        }
      }

      if (!firmInvite) {
        if (selectedPlan === 'unlimited') {
          localStorage.setItem('trueload_pending_plan', JSON.stringify({ plan: 'unlimited' }));
        } else if (selectedPlan === 'enterprise') {
          localStorage.setItem(
            'trueload_pending_enterprise',
            JSON.stringify({
              company_name: firmName,
              seat_count: seatCount,
              billing_cycle: billingCycle,
              take_seat: takeSeat,
            })
          );
        }
      }
      setSuccess(true);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    setResendMessage('');
    const { error: resendError } = await resendConfirmation(email);
    if (resendError) {
      setResendStatus('error');
      const m = resendError.message.toLowerCase();
      if (m.includes('rate') || m.includes('seconds') || m.includes('too many') || m.includes('limit')) {
        setResendMessage('Please wait a moment before requesting another email.');
      } else if (m.includes('already') || m.includes('confirmed')) {
        setResendMessage('This email is already confirmed — you can sign in.');
      } else {
        setResendMessage('Could not resend right now. Please try again in a minute.');
      }
    } else {
      setResendStatus('sent');
      setResendMessage('Sent! Check your inbox (and spam folder) again.');
      setResendCooldown(60);
    }
  };

  if (success) {
    const isEnterprise = selectedPlan === 'enterprise';
    const isUnlimited = selectedPlan === 'unlimited';
    const bodyText = firmInvite
      ? `We've sent a confirmation email. Click the link in that email to verify your account, then sign in to complete joining ${firmInvite.firm_name}.`
      : isEnterprise
        ? "We've sent a confirmation email. Click the link in that email to verify your account, then sign in to complete your firm account setup."
        : isUnlimited
          ? "We've sent a confirmation email. Click the link in that email to verify your account, then sign in to complete your subscription setup."
          : "We've sent a confirmation email to verify your account. Click the link in that email, then sign in to start using TrueLoad.";

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-gray-100 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Account Created!</h2>
            <p className="text-gray-600">{bodyText}</p>
            <p className="text-sm text-gray-500 mt-2">
              Sent to <span className="font-semibold text-gray-700">{email}</span>.{' '}
              <button
                type="button"
                onClick={() => { setSuccess(false); setResendStatus('idle'); setResendMessage(''); }}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Wrong email?
              </button>
            </p>
            <div className="mt-5 text-left bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-snug">
                <span className="font-semibold">Don't see it within a few minutes?</span> Check your spam or junk folder — confirmation emails sometimes get filtered there. If you find it, mark it <span className="font-semibold">"Not spam"</span> so future TrueLoad emails reach your inbox.
              </p>
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendStatus === 'sending' || resendCooldown > 0}
                data-testid="resend-confirmation"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendStatus === 'sending'
                  ? 'Sending…'
                  : resendCooldown > 0
                    ? `Resend available in ${resendCooldown}s`
                    : "Didn't get it? Resend confirmation email"}
              </button>
              {resendMessage && (
                <p className={`text-sm mt-2 ${resendStatus === 'error' ? 'text-red-600' : 'text-green-700'}`}>{resendMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 px-4 py-8">
      <div className="max-w-lg w-full">
        {firmInvite && (
          <div className="mb-4 bg-blue-800 text-white rounded-xl px-6 py-4 shadow-lg">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold text-sm">You've been invited to join {firmInvite.firm_name}</span>
            </div>
            <p className="text-blue-200 text-xs">Create your account to accept your invitation.</p>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <img
                src="/trueload_icon_512.png"
                alt="TrueLoad"
                width={64}
                height={64}
                className="w-16 h-16 rounded-2xl"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
            <p className="text-gray-600 mt-2">{firmInvite ? `Join ${firmInvite.firm_name} on TrueLoad` : 'Choose a plan and get started with TrueLoad'}</p>
          </div>

          {/* Plan selector — hidden for firm invites */}
          {firmInvite ? (
            <div className="mb-6">
              <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                Access is covered by your firm's Enterprise plan.
              </p>
            </div>
          ) : null}
          {!firmInvite && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {/* Free Trial */}
            <button
              type="button"
              data-testid="plan-selector-trial"
              onClick={() => setSelectedPlan('trial')}
              className={`relative text-left p-4 rounded-lg border-2 transition-all ${
                selectedPlan === 'trial'
                  ? 'ring-2 ring-blue-600 border-blue-600 bg-blue-50'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="font-semibold text-gray-900 text-sm mb-1">Free Trial</div>
              <div className="text-xs text-gray-500 leading-snug">
                Start free. Your first project is on us. Pay $30/project after that, or upgrade anytime.
              </div>
            </button>

            {/* Unlimited */}
            <button
              type="button"
              data-testid="plan-selector-unlimited"
              onClick={() => setSelectedPlan('unlimited')}
              className={`relative text-left p-4 rounded-lg border-2 transition-all ${
                selectedPlan === 'unlimited'
                  ? 'ring-2 ring-blue-600 border-blue-600 bg-blue-50'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between gap-1 mb-1">
                <div className="font-semibold text-gray-900 text-sm">Unlimited</div>
                <span className="shrink-0 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded font-medium">Popular</span>
              </div>
              <div className="text-xs text-gray-500 leading-snug">
                $59/month. Unlimited projects, no per-project fees. Cancel anytime.
              </div>
            </button>

            {/* Enterprise */}
            <button
              type="button"
              data-testid="plan-selector-enterprise"
              onClick={() => setSelectedPlan('enterprise')}
              className={`relative text-left p-4 rounded-lg border-2 transition-all ${
                selectedPlan === 'enterprise'
                  ? 'ring-2 ring-blue-800 border-blue-800 bg-blue-50'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="font-semibold text-gray-900 text-sm mb-1">Enterprise</div>
              <div className="text-xs text-gray-500 leading-snug">
                Custom pricing. Multi-user firm account with centralized billing and project management.
              </div>
            </button>
          </div>
          )}

          {error && (
            <div data-testid="signup-error" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {accountExists && (
            <div data-testid="signup-account-exists" className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p>An account with this email already exists. No email was sent.</p>
                <p className="mt-2">
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    data-testid="signup-account-exists-login"
                    className="font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Sign in
                  </button>
                  {' or '}
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    data-testid="signup-account-exists-reset"
                    className="font-semibold text-blue-600 hover:text-blue-700"
                  >
                    reset your password
                  </button>
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { if (!firmInvite) setEmail(e.target.value); }}
                  readOnly={!!firmInvite}
                  required
                  data-testid="signup-email"
                  className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${firmInvite ? 'bg-gray-50' : ''}`}
                  placeholder="you@example.com"
                />
                {firmInvite && (
                  <p className="text-xs text-gray-500 mt-1">This email was used for your invitation and cannot be changed.</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="signup-password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="At least 12 characters"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Re-enter password"
                />
              </div>
            </div>

            {/* Enterprise-only fields */}
            {selectedPlan === 'enterprise' && (
              <div className="space-y-5 pt-2 border-t border-gray-100">
                <p className="text-sm text-blue-800 font-medium">Firm Account Details</p>

                <div>
                  <label htmlFor="firmName" className="block text-sm font-medium text-gray-700 mb-2">
                    Firm Name
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="firmName"
                      type="text"
                      value={firmName}
                      onChange={(e) => setFirmName(e.target.value)}
                      required
                      maxLength={200}
                      data-testid="enterprise-firm-name"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Acme Electric LLC"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="seatCount" className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Users (seats)
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="seatCount"
                      type="number"
                      value={seatCount}
                      onChange={(e) => setSeatCount(clampSeats(parseInt(e.target.value, 10)))}
                      required
                      min={SEAT_MIN}
                      max={SEAT_MAX}
                      data-testid="enterprise-seat-count"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    A seat is what lets someone run calculations. Managing the firm is free, so
                    buy one seat per person who'll actually use TrueLoad.
                  </p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={takeSeat}
                    onChange={(e) => setTakeSeat(e.target.checked)}
                    data-testid="enterprise-take-seat"
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Give one of these seats to me
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Leave this off if you're setting the firm up for other people — you'll still
                      administer it, and you can assign every seat from the firm dashboard.
                    </span>
                  </span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="enterprise-billing-cycle-monthly"
                      onClick={() => setBillingCycle('monthly')}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        billingCycle === 'monthly'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      data-testid="enterprise-billing-cycle-annual"
                      onClick={() => setBillingCycle('annual')}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        billingCycle === 'annual'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Annual (save {annualDiscountPercent()}%)
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <input
                id="agree"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="signup-agree"
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="agree" className="text-sm text-gray-600 leading-snug">
                I have read and agree to the{' '}
                <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</a>
                {' '}and{' '}
                <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</a>.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !agreed}
              data-testid="signup-submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={onSwitchToLogin}
                data-testid="signup-switch-login"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
